import { useCallback, useMemo, memo } from "react";
import { Popup } from "components/UI/Popup";
import { NicheOption } from "./NicheOption";
import { CheckboxOption } from "./utils/helpers";
import { clsx } from "clsx";
import { ALLOWED_NICHES, ERROR_GET_NICHES, EMPTY_NICHES_MESSAGE, MAX_NICHES_MESSAGE } from "./utils/constants";
import "./styles/AddToNichePopup.scss";

interface IAddToNichePopupProps {
  isOpen: boolean;
  togglePopup: () => void;
  isError: boolean;
  niches: CheckboxOption[];
  onChange: (indx: number, available: boolean) => void;
  onBtnClick: () => void;
}

export const AddToNichePopup = memo((props: IAddToNichePopupProps) => {
  const {
    isOpen,
    togglePopup,
    isError,
    niches,
    onChange,
    onBtnClick
  } = props;

  const checkedCount = useMemo(() => niches.filter(chk => chk.isChecked).length, [niches]);
  const defaultCheckedCount = useMemo(() => niches.filter(chk => chk.disabled).length , [niches]);
  const nichesAvailable = niches.length > 0;

  const renderCnt = useCallback(() => {
    if (isError) {
      return <p>{ERROR_GET_NICHES}</p>;
    }

    if (!nichesAvailable) {
      return <p>{EMPTY_NICHES_MESSAGE}</p>;
    }

    return (
      <div data-testid="niche-popup-cnt">
        <p className="Title" data-testid="title">
          {checkedCount < ALLOWED_NICHES ?
            `Content can be added to ${ALLOWED_NICHES - checkedCount} more niche${ALLOWED_NICHES - checkedCount > 1 ? "s" : ""}` :
            MAX_NICHES_MESSAGE}
        </p>
        {niches.map((niche, index) => <NicheOption
          key={niche.name}
          indx={index}
          niche={niche}
          onChange={onChange}
        />)}
      </div>
    );
  }, [isError, checkedCount, onChange, niches, nichesAvailable]);

  const buttonDisabled = nichesAvailable ?
    (checkedCount - defaultCheckedCount <= 0 || checkedCount > ALLOWED_NICHES) : false;

  const buttonLabel = useMemo(() => {
    if (!nichesAvailable) {
      return "Browse Niches";
    }
    const nichesToAddCount = checkedCount - defaultCheckedCount;
    if (nichesToAddCount <= 0) {
      return "Add to Niche(s)";
    }
    return `Add to ${nichesToAddCount} Niche${nichesToAddCount > 1 ? "s" : ""}`;
  }, [nichesAvailable, checkedCount, defaultCheckedCount]);

  return (
    <Popup
      title="Add to Niche(s)"
      isOpen={isOpen}
      popupClass={clsx("AddToNichePopup", !nichesAvailable && "AddToNichePopup_isEmpty")}
      btnProps={!isError ? {
        variant: 1,
        title: buttonLabel,
        onClick: onBtnClick,
        disabled: buttonDisabled
      } : undefined}
      onClose={togglePopup}
    >
      {renderCnt()}
    </Popup>
  );
});
